import { Input, Paginate, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useState, useEffect } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import BulkActions from './bulk-vendor-actions';
import { BaseMealFormType, BaseMealType } from 'models/pricing.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import usePricingContext from 'contexts/pricing.context';
import { ReactComponent as NoProcurement } from 'assets/icons/no-procurement.svg';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import useRoleContext from 'contexts/role.context';
import { InventoryModule } from 'models/permission.model';
import { EDIT_PERMISSION_ACTIONS } from 'utils/authorization';

const BaseMeals = () => {
  const navigate = useNavigate();
  const { can } = useRoleContext();
  const { setStep, setBaseMealFormData, setSelectedBaseMeal } = usePricingContext();
  const [selectedItems, setSelectedItems] = useState<BaseMealType[]>([]);
  const [baseMeals, setBaseMeals] = useState<BaseMealType[]>([]);
  const [isFetchingBaseMeals, setIsFetchingBaseMeals] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showBulkActions, setShowBulkActions] = useState<boolean>(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const canEditMealPricing = can(InventoryModule.MealAddOnPricing, EDIT_PERMISSION_ACTIONS);

  const sortConvertionItem = (item: BaseMealType) => {
    const sortedArray: BaseMealFormType = {
      base_meal_id: item?.id,
      name: item?.name,
      items:
        item?.link?.items.map((item) => ({
          mix_id: item.mix_id,
          quantity: item.quantity,
          meal: item.mix
        })) ?? []
    };
    return sortedArray;
  };
  const handleEdit = (item?: BaseMealType) => {
    if (!canEditMealPricing) return;
    const items: BaseMealFormType[] = item
      ? [sortConvertionItem(item)]
      : selectedItems?.map((_) => sortConvertionItem(_));
    const itemsSelected = {
      ...items[0],
      itemIndex: 0
    };
    navigate(`${MAIN_STORE_ROUTE_KEYS.UPDATE_BASE_MEALS}`, {
      state: items
    });
    setBaseMealFormData(items);
    setSelectedBaseMeal(itemsSelected);
    setStep(1);
  };

  useEffect(() => {
    if (selectedItems.length > 1) {
      setShowBulkActions(true);
    } else {
      setShowBulkActions(false);
    }
  }, [selectedItems]);

  const clearVendorsList = () => {
    setBaseMeals([]);
    setSelectedItems([]);
  };

  const getBaseMeals = async () => {
    setIsFetchingBaseMeals(true);

    try {
      const res: any = await apiService.get('meal_link', {
        version: 'v2',
        params: {
          size: 25,
          page: currentPage,
          status: activeFilter === 'all' ? undefined : activeFilter,
          name: search
        }
      });
      const response = res?.data?.data;
      setBaseMeals(response);
      setLastPage(Math.floor(res?.data?.pagination.total / res?.data?.pagination.page_size));
    } catch (error: any) {
      toast(<ToastMessage type='error' message={error.response.data.message} />);
    } finally {
      setIsFetchingBaseMeals(false);
    }
  };
  useEffect(() => {
    getBaseMeals();
  }, [activeFilter, currentPage, search]);

  return (
    <div className='px-2 md:px-6 mt-2 mb-10 vendors'>
      <div className='flex justify-between items-center mb-8'>
        <div>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>Base Meals</h4>
          <p className='text-[#6F7376] text-sm'>Make operations seamlessly by mapping base meals</p>
        </div>
        <div className={` bg-gray-1 rounded-full flex gap-4 py-1 px-3 text-sm`}>
          <button
            onClick={() => setActiveFilter('all')}
            className={`${activeFilter === 'all' && 'bg-white'} rounded-full py-1 px-3`}>
            All
          </button>
          <button
            onClick={() => {
              setActiveFilter('linked');
              setCurrentPage(1);
            }}
            className={`${activeFilter === 'linked' && 'bg-white'} rounded-full py-1 px-3`}>
            Mapped
          </button>
          <button
            onClick={() => {
              setActiveFilter('unlinked');
              setCurrentPage(1);
            }}
            className={` ${activeFilter === 'unlinked' && 'bg-white'} rounded-full py-1 px-3`}>
            Unmapped
          </button>
        </div>
      </div>

      <div className='flex justify-between items-center mb-8'>
        <div className='flex gap-3 items-center'>
          <Input
            type='search'
            placeholder='Enter meal name'
            className='w-[300px] search-input'
            inputClass='h-[36px]'
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <Paginate
          pageCount={lastPage}
          onPageChange={(e) => {
            clearVendorsList();
            setCurrentPage(e.selected + 1);
          }}
        />
      </div>

      {isFetchingBaseMeals ? (
        <div className='pt-10'>
          <ActivityIndicator />
        </div>
      ) : baseMeals?.length === 0 ? (
        <div className='grid place-items-center mt-24'>
          <NoProcurement className='mb-5' />
          <p className='font-medium text-[#5E6164]'>There are currenly no base meals</p>
        </div>
      ) : (
        <div className=' gap-7 grid grid-cols-2 max-[768px]:grid-cols-1'>
          {baseMeals.map((item, i) => (
            <div
              key={i}
              onClick={() => {
                if (!canEditMealPricing) return;
                handleEdit(item);
              }}
              className={`item-card bg-white rounded-lg px-3 py-2 flex gap-2 justify-between items-center shadow-[0px_6px_12px_0px_rgba(136,140,145,0.15)] ease-in-out transition-colors duration-300 ${
                canEditMealPricing ? 'cursor-pointer' : 'cursor-default'
              }`}>
              <div className='flex justify-start items-center gap-3'>
                <span className='text-base font-semibold'>{item.name}</span>

                {!item.link ? (
                  <span className='text-sm text-gray-12 bg-gray-13 px-4 py-0.5 rounded-full'>
                    Unmapped
                  </span>
                ) : (
                  <span className='text-sm text-white bg-green-6 px-4 py-0.5 rounded-full'>
                    Mapped
                  </span>
                )}
              </div>
              <button className='flex justify-center items-center gap-1 bg-white w-[30px] h-[30px] rounded-full opacity-0'>
                <EditIcon className='w-[18px] h-[18px]' />
              </button>
            </div>
          ))}
        </div>
      )}

      {canEditMealPricing && selectedItems.length !== 0 && (
        <BulkActions
          item={selectedItems}
          editFunc={handleEdit}
          showBulkActions={showBulkActions}
          setShowBulkActions={setShowBulkActions}
          setSelectedItems={setSelectedItems}
        />
      )}
    </div>
  );
};

export default BaseMeals;
